import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as Commons from "oc-commons/Commons";
import { I18N } from "oc-I18N";
import { MAuswahlwert } from "../../../metamodell";
import { FormType } from "../../arrangement/Grundriss";

interface StandardFormPopoverProps {
  anchorRef: React.MutableRefObject<HTMLElement>;
  removeDialogFunction: () => void;
}

export const StandardFormPopover = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<StandardFormPopoverProps>
>(({ anchorRef, removeDialogFunction }, forwardedRef) => {
  const [style, setStyle] = useState<{ top: number }>({ top: 0 });

  const containerRef = useRef<HTMLDivElement | null>(null);
  useImperativeHandle(forwardedRef, () => containerRef.current as HTMLDivElement);

  useEffect(() => {
    function updatePosition() {
      const anchorEl = anchorRef.current;
      const parentEl = anchorEl?.parentElement?.parentElement;
      if (!anchorEl || !parentEl) return;
      const anchorRect = anchorEl.getBoundingClientRect();
      const parentRect = parentEl.getBoundingClientRect();
      const relativeTop = anchorRect.top - parentRect.top;
      setStyle({ top: relativeTop });
    }
    updatePosition();
    Commons.sleep(1000).then(updatePosition);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorRef]);

  // Nach dem Öffnen den Fokus auf den ersten Grundriss-Button setzen
  useEffect(() => {
    if (!containerRef.current) return;
    const firstButton = containerRef.current.querySelector<HTMLButtonElement>(
      ".StandardGrundrissForm-button"
    );
    firstButton?.focus();
  }, []);

  const closeAndReturnFocus = () => {
    removeDialogFunction();
    anchorRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className="StandardGrundrissForm-dialog"
      style={style}
      role="dialog"
      aria-modal="false"
      aria-label={I18N("Bitte wählen Sie die Form ihres Zaunes im Grundriss")}
      data-group-key="Grundform-Auswahl"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          e.preventDefault();
          closeAndReturnFocus();
          return;
        }

        if (e.key === "Tab" && !e.shiftKey && containerRef.current) {
          const buttons = Array.from(
            containerRef.current.querySelectorAll<HTMLButtonElement>(
              ".StandardGrundrissForm-button"
            )
          );
          const active = document.activeElement as HTMLElement | null;
          const lastButton = buttons[buttons.length - 1];

          if (active === lastButton) {
            e.preventDefault();
            closeAndReturnFocus();
          }
        }
      }}
    >
      <div className="auswahl-container">
        {APP.metadata.StandardGrundrissForm_Auswahlwert.map((form: MAuswahlwert, index: number) => (
          <StandardGrundrissForm
            key={form.id}
            form={form}
            index={index}
            removeDialogFunction={closeAndReturnFocus}
          />
        ))}
      </div>
    </div>
  );
});

function StandardGrundrissForm({
  form,
  index,
  removeDialogFunction,
}: {
  form: MAuswahlwert;
  index: number;
  removeDialogFunction: () => void;
}) {
  return (
    <button
      className={`StandardGrundrissForm-button flex-center cell-${index} page-fade-out-on-exit`}
      data-button-key={form.Name || form.Key}
      onClick={async () => {
        const formType = form.Key.substringAfter("StandardForm:") as FormType;
        APP.arrangement.grundriss.setStandardForm(formType);
        APP.updateUI();
        APP.logUserAction({ action: "setStandardForm", Form: formType });
        removeDialogFunction();
      }}
    >
      <div
        className="StandardGrundrissForm-button__img fade"
        style={{ backgroundImage: `url(${form.Auswahlbild})` }}
      ></div>
      <span className="StandardGrundrissForm-button__label">{form.Name}</span>
    </button>
  );
}
